import {
    Card,
    Colors,
    Heading,
    Icon,
    ItemList,
    Pills,
    Text,
    type ListItemData,
} from '@amelio/ui-library';

import { FeedbackSettingsPills } from '@/components/FeedbackSettingsPills/FeedbackSettingsPills';
import {
    isAnonymousFeedback,
    revieweeLaunchedCopy,
    type FeedbackPrivacyValue,
} from '@/constants/review/FeedbackPrivacy';
import { useAmelioTranslations } from '@/hooks';

interface ResultsVisibilityProps {
    managerAccessList: ListItemData[];
    /** Given for a 360, whose mode decides what its results show of the respondents. */
    feedbackPrivacy?: FeedbackPrivacyValue;
    /** Given for a 360: one whose comments take no reply says so beside its mode. */
    commentRepliesEnabled?: boolean;
    /** The person evaluated launched this 360 and reads its results. */
    launchedByReviewee?: boolean;
}

const ResultsVisibility = ({
    managerAccessList,
    feedbackPrivacy,
    commentRepliesEnabled,
    launchedByReviewee = false,
}: ResultsVisibilityProps) => {
    const { t } = useAmelioTranslations();
    const isAnonymous = isAnonymousFeedback(feedbackPrivacy);
    const launchedEmployeeAccessKey = isAnonymous
        ? revieweeLaunchedCopy(feedbackPrivacy)
        : 'evaluation360ScheduleCreation.resultsVisibility.revieweeSeesNamedAnswers';
    const employeeAccessKey = launchedByReviewee
        ? launchedEmployeeAccessKey
        : 'evaluation360ScheduleCreation.resultsVisibility.noAccessMessage';

    return (
        <Card
            className={'mt-12'}
            bgColor={'bg-white'}
            borderRadius={'2xl'}
            padding={'xl'}
        >
            <div className={'flex flex-wrap items-center gap-y-2'}>
                <Icon
                    name={'security-eye'}
                    size={'xl'}
                    className={'mr-3'}
                />

                <Heading level={5}>
                    {t('evaluation360ScheduleCreation.resultsVisibility.title')}
                </Heading>

                <FeedbackSettingsPills
                    feedbackPrivacy={feedbackPrivacy}
                    commentRepliesEnabled={commentRepliesEnabled}
                    className={'ml-3'}
                    dataTestId={'results-visibility-feedback-settings'}
                />
            </div>

            <div className={'mt-8 flex flex-row justify-between gap-2'}>
                <Text className={'flex-1 sm:flex-none'}>
                    {t('evaluation360ScheduleCreation.resultsVisibility.managerAccess')}
                </Text>

                {!isAnonymous && (
                    <Pills
                        size={'md'}
                        variant={'primary'}
                    >
                        {t('evaluation360ScheduleCreation.resultsVisibility.fullAccess')}
                    </Pills>
                )}
            </div>

            <ItemList
                items={managerAccessList.map((item) => ({
                    ...item,
                    content: <Text>{item.content}</Text>,
                }))}
                size={'sm'}
                density={'compact'}
                className={'mt-4'}
            />

            <div className={'mt-10'}>
                <Text>{t('evaluation360ScheduleCreation.resultsVisibility.employeeAccess')}</Text>

                <Text
                    size={'small'}
                    color={Colors.BASE}
                    className={'mt-2'}
                >
                    {t(employeeAccessKey)}
                </Text>
            </div>
        </Card>
    );
};

export { ResultsVisibility };
